/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

const FAQS = [
    {
        q: "Est-ce que Homly remplace mon CRM ?",
        a: "Non, Homly est complémentaire. Vous continuez à signer vos mandats dans votre logiciel habituel, puis vous envoyez simplement le PDF dans Homly : l'IA extrait les informations essentielles en quelques secondes. Vous gagnez ce qu'aucun CRM ne fait : pilotage des KPI métier, copilotes IA spécialisés (Pilote pour le dirigeant, Coach pour le négociateur, Confident pour le vendeur, Providence pour les groupes multi-agences), détection des signaux faibles, et espace vendeur transparent. Homly est une surcouche, pas un remplaçant.",
    },
    {
        q: "Faut-il que mes équipes apprennent un nouvel outil ?",
        a: "Soyons honnêtes : oui, Homly est un nouvel outil. Nous l'avons conçu pour qu'il soit le plus simple possible à prendre en main — logique guidée, écrans pensés pour aller à l'essentiel, vocabulaire métier. Côté terrain, vos négociateurs intègrent quelques gestes nouveaux dans leur quotidien (consigner une visite, un échange vendeur, une offre) — c'est ce qui alimente vos indicateurs et donne sa valeur à l'IA. Côté dirigeant, le cockpit synthétise tout en une vue claire. L'objectif : effort minimal pour vos équipes, valeur ajoutée maximale sur le pilotage et la structuration.",
    },
    {
        q: "Mes commerciaux vont-ils devoir ressaisir leurs données ?",
        a: "L'import des mandats est automatique : envoyez le PDF signé, l'IA extrait les informations clés en quelques secondes (vendeur, adresse, prix, honoraires, durée, type). En revanche, pour que vos KPI soient riches et que les copilotes IA soient vraiment pertinents, vos négociateurs doivent consigner les actions clés du suivi : visites, échanges vendeur, offres reçues. Ces saisies sont rapides, guidées, et représentent l'effort minimal nécessaire pour transformer votre pilotage. Sans ces données terrain, le cockpit reste vide — avec elles, il devient un véritable outil de décision.",
    },
    {
        q: "C'est quoi exactement les copilotes IA ?",
        a: "Quatre IA spécialisées qui travaillent sur vos données. Pilote accompagne le dirigeant : signaux faibles invisibles (silences, vagues d'inquiétude prix, surcharges agents), évaluation du risque de perte sur chaque mandat, briefing matinal + synthèse hebdomadaire. Coach accompagne le négociateur : préparation de rendez-vous vendeur en quelques secondes (points-clés + script), coaching hebdomadaire, priorisation par impact €. Confident accompagne le vendeur : réponses 24h/24 dans son espace, analyse du ressenti dans chaque message, alerte manager si signaux de départ. Providence (groupes multi-agences) : analyse stratégique réseau, recommandations de pilotage, sans micro-management.",
    },
    {
        q: "L'IA peut-elle se tromper ?",
        a: "La marge d'erreur de Homly est très faible. Sur les chiffres-clés (KPI mandats, honoraires, délais, taux), les valeurs sont calculées directement à partir de vos données : aucune invention possible. Pour les analyses et recommandations, chaque réponse est vérifiée automatiquement contre vos données réelles avant d'être affichée, et l'IA préfère dire qu'elle ne sait pas plutôt que d'inventer. Mieux : l'IA s'enrichit avec votre utilisation. À chaque réponse, un simple vote 👍 / 👎 permet de l'affiner — elle devient plus précise et plus pertinente sur votre agence au fil du temps. Système auto-critique, auto-correcteur, pensé pour vous accompagner sans vous faire perdre de temps.",
    },
    {
        q: "Combien de temps pour être opérationnel ?",
        a: "L'onboarding prend 7 à 14 jours ouvrés. Notre équipe configure votre compte agence, importe vos premiers mandats à partir des PDF, paramètre vos objectifs et personnalise vos KPI. La prise en main initiale par vos équipes dure 30 minutes à 1 heure selon les profils — l'outil est volontairement guidé et lisible dès la première connexion. Vous démarrez avec des indicateurs immédiatement exploitables, et un accompagnement personnalisé reste actif pendant les 30 premiers jours pour ancrer les bons réflexes.",
    },
    {
        q: "Mes données sont-elles sécurisées ?",
        a: "Chiffrement AES-256 (le même niveau que votre banque), accès protégés, sauvegardes quotidiennes, conformité RGPD totale. Vos données vous appartiennent et restent exportables à tout moment au format CSV ou PDF. Nous ne partageons jamais vos données avec d'autres agences.",
    },
    {
        q: "Pourquoi demander une démo plutôt qu'un essai direct ?",
        a: "Pendant la démo de 15 minutes, nous comprenons votre organisation et vous montrons Homly avec un compte de démonstration pré-rempli : mandats actifs, ventes du mois, négociateurs réels, vendeur en risque, alertes IA en direct. Vous voyez concrètement ce que l'outil dira de vos chiffres dès la première semaine. Vous repartez avec une vision claire, pas un argumentaire commercial.",
    },
];

/**
 * ConvFAQ — FAQ dark-luxury intégrée au funnel Conv*.
 * Première question : objection CRM (la plus convertissante) — pré-ouverte.
 * Deuxième : adoption équipes (renforce le positionnement cockpit/surcouche).
 */
export function ConvFAQ() {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section
            id="faq"
            className="relative py-20 md:py-28 bg-[#08080f] border-t border-white/[0.04] overflow-hidden"
            data-testid="conv-faq"
        >
            {/* Ambient glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#681793]/[0.06] rounded-full blur-[140px]" />
            </div>

            <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <span
                        className="inline-block px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/55 mb-5"
                        style={DISPLAY}
                    >
                        Vos questions
                    </span>
                    <h2
                        className="text-3xl md:text-4xl font-bold text-white tracking-tight"
                        style={DISPLAY}
                    >
                        Les réponses dont vous avez besoin{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            avant de vous lancer.
                        </span>
                    </h2>
                </motion.div>

                <div className="space-y-2.5">
                    {FAQS.map((faq, idx) => {
                        const isOpen = openIndex === idx;
                        return (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.03, duration: 0.4 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                                    className={`w-full text-left p-5 rounded-2xl border transition-all duration-300 ${
                                        isOpen
                                            ? 'bg-gradient-to-br from-white/[0.05] to-white/[0.02] border-white/15'
                                            : 'bg-white/[0.025] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/10'
                                    }`}
                                    data-testid={`conv-faq-item-${idx}`}
                                    aria-expanded={isOpen}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <span
                                            className={`font-semibold pr-4 text-sm sm:text-base leading-snug transition-colors ${
                                                isOpen ? 'text-white' : 'text-white/85'
                                            }`}
                                            style={DISPLAY}
                                        >
                                            {faq.q}
                                        </span>
                                        <ChevronDown
                                            className={`w-5 h-5 shrink-0 mt-0.5 transition-all duration-300 ${
                                                isOpen
                                                    ? 'rotate-180 text-[#FF914D]'
                                                    : 'text-white/40'
                                            }`}
                                        />
                                    </div>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                                className="overflow-hidden"
                                            >
                                                <p className="mt-4 text-sm leading-relaxed text-white/65">
                                                    {faq.a}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </button>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
